import { DatePicker, Form, Input, Select, Tooltip } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import generatePicker from 'antd/es/date-picker/generatePicker';
import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import startCase from 'lodash/startCase';
import CloseIcon from 'assets/svg/CloseIcon';
import type {
    FormFieldConfig,
    DimSelectFieldConfig,
    SelectFieldConfig,
    YearPickerFieldConfig,
} from '../config/tpoGenConfig';
import { DimSelect } from './DimSelect';
import type { UseDimOptionsHook } from './DimSelect';

export type { UseDimOptionsHook, UseDimOptionsParams, UseDimOptionsResult, DimSelectProps } from './DimSelect';
export { DimSelect } from './DimSelect';

const YearPicker = generatePicker<Dayjs>(dayjsGenerateConfig);

const FieldShell = ({
    label,
    children,
    className = 'w-[26rem]',
}: {
    label: string;
    children: ReactNode;
    className?: string;
}) => (
    <div className={`${className} flex flex-col gap-y-1`}>
        <p>{label}</p>
        {children}
    </div>
);

export const TagComponent = ({ name, onRemove }: { name: string; onRemove?: () => void }) => {
    return (
        <div className="flex flex-row w-[5.75rem] bg-[#F4F4F5] rounded-md p-2 items-center justify-between">
            <Tooltip title={name} showArrow={false}>
                <p className="text-xs truncate font-medium">{name}</p>
            </Tooltip>
            <span className="cursor-pointer" onClick={onRemove}>
                <CloseIcon size={8} />
            </span>
        </div>
    );
};

export const TagFieldRow = ({
    field,
    values,
    onRemoveTag,
    parentValue,
    useDimOptions,
}: {
    field: DimSelectFieldConfig;
    values: string[];
    onRemoveTag: (fieldName: string, value: string) => void;
    parentValue?: string;
    useDimOptions: UseDimOptionsHook;
}) => {
    const fieldName = `${field.dimension}-${field.attributeType}`;

    const { options } = useDimOptions({
        dimension: field.dimension,
        attributeType: field.attributeType,
        lowestAttribute: field.lowestAttribute,
        filters: {},
        selectedDimension: field.dependsOn
            ? {
                  value: parentValue ?? '',
                  dimension: field.dependsOn.dimension,
                  attributeType: field.dependsOn.attributeType,
              }
            : { value: '', dimension: field.dimension, attributeType: field.attributeType },
        searchString: '',
    });

    const valueLabelMap = useMemo(() => {
        const map = new Map<string, string>();
        for (const opt of options) {
            map.set(String(opt.value), opt.label);
        }
        return map;
    }, [options]);

    return (
        <div className="flex flex-col gap-y-2">
            <p className="text-xs text-[#52525C]">{`${startCase(field.attributeType)}s Selected`}</p>
            <div className="flex flex-row flex-wrap gap-2">
                {values.map((val) => (
                    <TagComponent
                        key={val}
                        name={valueLabelMap.get(String(val)) ?? String(val)}
                        onRemove={() => onRemoveTag(fieldName, val)}
                    />
                ))}
            </div>
        </div>
    );
};

const YEAR_DISABLED_DATE_FNS: Record<string, (date: Dayjs) => boolean> = {
    currentAndNext: (date) => !(date.year() === dayjs().year() || date.year() === dayjs().year() + 1),
};

const SelectFieldRenderer = ({ field, fieldClassName }: { field: SelectFieldConfig; fieldClassName?: string }) => (
    <FieldShell label={field.label} className={fieldClassName}>
        <Form.Item
            label=""
            name={field.name}
            rules={field.required ? [{ required: true, message: `${field.label} is required` }] : undefined}
        >
            <Select placeholder={field.placeholder} options={field.options} />
        </Form.Item>
    </FieldShell>
);

const YearPickerFieldRenderer = ({
    field,
    fieldClassName,
}: {
    field: YearPickerFieldConfig;
    fieldClassName?: string;
}) => {
    const disabledDate = field.allowedYears ? YEAR_DISABLED_DATE_FNS[field.allowedYears] : undefined;
    return (
        <FieldShell label={field.label} className={fieldClassName}>
            <Form.Item
                label=""
                name={field.name}
                rules={field.required ? [{ required: true, message: `${field.label} is required` }] : undefined}
            >
                <YearPicker
                    placeholder={field.placeholder}
                    allowClear={false}
                    style={{ width: '100%', borderRadius: 6 }}
                    disabledDate={disabledDate}
                    picker="year"
                />
            </Form.Item>
        </FieldShell>
    );
};

export interface ConfigDrivenFormFieldsProps {
    fields: FormFieldConfig[];
    isEditMode?: boolean;
    useDimOptions: UseDimOptionsHook;
    enabled?: boolean;
    fieldClassName?: string;
}

const ConfigDrivenFormFields = ({
    fields,
    isEditMode,
    useDimOptions,
    enabled = true,
    fieldClassName = 'w-[26rem]',
}: ConfigDrivenFormFieldsProps) => {
    const form = Form.useFormInstance();
    const prevParentKey = useRef('');

    const dimSelectFields = fields.filter((f): f is DimSelectFieldConfig => f.type === 'dimSelect');
    const dependentFields = dimSelectFields.filter((f) => f.dependsOn);
    const tagFields = dimSelectFields.filter((f) => f.showSelectedTags);

    const parentFieldNames = dependentFields.map((f) => f.dependsOn!.fieldName);
    const tagFieldNames = tagFields.map((f) => `${f.dimension}-${f.attributeType}`);

    const allValues: Record<string, any> = Form.useWatch([], form) ?? {};

    const watchedParents: Record<string, string> = {};
    for (const name of parentFieldNames) {
        watchedParents[name] = allValues[name] ?? '';
    }

    const watchedTagValues: Record<string, string[]> = {};
    for (const name of tagFieldNames) {
        watchedTagValues[name] = allValues[name] ?? [];
    }

    const currentParentKey = JSON.stringify(watchedParents);

    useEffect(() => {
        const prev = prevParentKey.current;

        if (currentParentKey === prev) return;

        const prevValues: Record<string, any> = prev ? JSON.parse(prev) : {};
        const allPrevEmpty = Object.values(prevValues).every(
            (v) => !v || v === '' || (Array.isArray(v) && v.length === 0),
        );

        const currentValues: Record<string, any> = JSON.parse(currentParentKey);
        const allCurrentEmpty = Object.values(currentValues).every(
            (v) => !v || v === '' || (Array.isArray(v) && v.length === 0),
        );

        prevParentKey.current = currentParentKey;

        if (isEditMode && allPrevEmpty) return;

        for (const field of dependentFields) {
            const fieldName = field.name ?? `${field.dimension}-${field.attributeType}`;
            if (field.isMultiSelect && !allCurrentEmpty) {
                continue;
            }
            form.setFieldsValue({ [fieldName]: field.isMultiSelect ? [] : undefined });
        }
    }, [currentParentKey, form]);

    const handleRemoveTag = (fieldName: string, value: string) => {
        const current: string[] = form.getFieldValue(fieldName) ?? [];
        form.setFieldsValue({ [fieldName]: current.filter((v) => v !== value) });
    };

    const renderField = (field: FormFieldConfig, index: number) => {
        switch (field.type) {
            case 'input':
                return (
                    <FieldShell key={index} label={field.label} className={fieldClassName}>
                        <Form.Item
                            label=""
                            name={field.name}
                            rules={
                                field.required ? [{ required: true, message: `${field.label} is required` }] : undefined
                            }
                        >
                            <Input placeholder={field.placeholder} />
                        </Form.Item>
                    </FieldShell>
                );
            case 'datepicker':
                return (
                    <FieldShell key={index} label={field.label} className={fieldClassName}>
                        <Form.Item
                            name={field.name}
                            rules={
                                field.required ? [{ required: true, message: `${field.label} is required` }] : undefined
                            }
                        >
                            <DatePicker
                                disabled={field.disabled}
                                placeholder={field.placeholder}
                                allowClear={false}
                                style={{ width: '100%', borderRadius: 6 }}
                                picker="date"
                            />
                        </Form.Item>
                    </FieldShell>
                );
            case 'select':
                return <SelectFieldRenderer key={index} field={field} fieldClassName={fieldClassName} />;
            case 'yearPicker':
                return <YearPickerFieldRenderer key={index} field={field} fieldClassName={fieldClassName} />;
            case 'dimSelect': {
                const resolvedFieldName = field.name ?? `${field.dimension}-${field.attributeType}`;
                const resolvedLabel = field.label ?? startCase(field.attributeType);
                const parentValue = field.dependsOn ? watchedParents[field.dependsOn.fieldName] : undefined;

                return (
                    <FieldShell key={index} label={resolvedLabel} className={fieldClassName}>
                        <Form.Item
                            label=""
                            name={resolvedFieldName}
                            rules={
                                field.required
                                    ? [{ required: true, message: `${resolvedLabel} is required` }]
                                    : undefined
                            }
                        >
                            <DimSelect
                                dimension={field.dimension}
                                attributeType={field.attributeType}
                                lowestAttribute={field.lowestAttribute}
                                isMultiSelect={field.isMultiSelect}
                                placeholder={`Select ${resolvedLabel}`}
                                useDimOptions={useDimOptions}
                                enabled={enabled}
                                selectedDimension={
                                    field.dependsOn
                                        ? {
                                              value: parentValue ?? '',
                                              dimension: field.dependsOn.dimension,
                                              attributeType: field.dependsOn.attributeType,
                                          }
                                        : undefined
                                }
                            />
                        </Form.Item>
                    </FieldShell>
                );
            }
        }
    };

    return (
        <>
            <div className="flex flex-row items-start flex-wrap gap-x-6 gap-y-4">{fields.map(renderField)}</div>
            <div className="flex flex-col gap-y-2">
                {tagFields.map((field) => {
                    const fieldName = `${field.dimension}-${field.attributeType}`;
                    const values = watchedTagValues[fieldName];
                    if (!values || values.length === 0) return null;
                    const parentValue = field.dependsOn ? watchedParents[field.dependsOn.fieldName] : undefined;
                    return (
                        <TagFieldRow
                            key={fieldName}
                            field={field}
                            values={values}
                            onRemoveTag={handleRemoveTag}
                            parentValue={parentValue}
                            useDimOptions={useDimOptions}
                        />
                    );
                })}
            </div>
        </>
    );
};

export default ConfigDrivenFormFields;
